import type { ChangeEvent } from 'react';
import { useMemo } from 'react';
import { handleImageUpload, removeImage, createImagePreviews } from '../utils/imageUtils';
import { Section, Divider } from './common/SharedStyles';
import { SPACING } from './common/constants';
import {
  renderProductIntro,
  renderRatingSection,
  renderSkinInfoSection,
  renderReviewContentSection,
  renderTitleSection,
  renderImageUploadSection,
  renderRecommendationSection,
  renderNicknameSection,
  renderTermsSection,
  renderSubmitSection,
} from './common/formHelpers';
import type { ReviewFormProps } from './common/interfaces';

const MobileSection = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Section style={{ marginBottom: SPACING['2xl'], ...style }}>{children}</Section>
);

const MobileDivider = ({ children }: { children?: React.ReactNode }) => (
  <Divider style={{ margin: `${SPACING.xl} 0` }}>{children}</Divider>
);

interface ReviewWriteFormProps extends ReviewFormProps {
  variant: 'mobile' | 'pc';
}

export const ReviewWriteForm = ({
  formData,
  formActions,
  options,
  productImage,
  isSubmitDisabled,
  onSubmit,
  variant,
}: ReviewWriteFormProps) => {
  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e, formData.images, formActions.setImages);
  };

  const handleRemoveImage = (idx: number) => {
    removeImage(idx, formData.images, formActions.setImages);
  };

  const imagePreviews = useMemo(() => createImagePreviews(formData.images), [formData.images]);

  // Use mobile-specific components for mobile variant
  const SectionComponent = variant === 'mobile' ? MobileSection : undefined;
  const DividerComponent = variant === 'mobile' ? MobileDivider : Divider;

  return (
    <>
      {renderProductIntro({ productImage })}
      <DividerComponent />
      {renderRatingSection({
        rating: formData.rating,
        setRating: formActions.setRating,
        SectionComponent,
      })}
      {renderSkinInfoSection({
        skinType: formData.skinType,
        setSkinType: formActions.setSkinType,
        skinConcern: formData.skinConcern,
        setSkinConcern: formActions.setSkinConcern,
        skinTypeOptions: options.skinTypeOptions,
        skinConcernOptions: options.skinConcernOptions,
        SectionComponent,
      })}
      {renderReviewContentSection({
        content: formData.content,
        setContent: formActions.setContent,
        SectionComponent,
      })}
      {renderTitleSection({
        title: formData.title,
        setTitle: formActions.setTitle,
        SectionComponent,
      })}
      {renderImageUploadSection({
        images: formData.images,
        imagePreviews,
        handleImageChange,
        handleRemoveImage,
        SectionComponent,
      })}
      {renderRecommendationSection({
        recommend: formData.recommend,
        setRecommend: formActions.setRecommend,
        SectionComponent,
      })}
      {renderNicknameSection({
        nickname: formData.nickname,
        setNickname: formActions.setNickname,
        SectionComponent,
      })}
      {renderTermsSection({ SectionComponent })}
      {renderSubmitSection({ isSubmitDisabled, onSubmit })}
    </>
  );
};
